<script lang="ts">
    import TinyEditor from './tiny-editor.svelte'

    export let data: Record<string, any>

    if (data.text === undefined) data.text = 'Texto de apoio exibido para quem está respondendo o formulário.'

    // Agrupadas como apareciam antes (cada grupo era separado por um <br />) — pessoa, endereço,
    // data/hora, atendimento, venda vinculada, pagamento, conta vinculada.
    const tagGroups: { tag: string; title: string }[][] = [
        [
            { tag: '{nome}', title: 'Nome completo da pessoa' },
            { tag: '{primeironome}', title: 'Primeiro nome da pessoa' },
            { tag: '{celular}', title: 'Celular da pessoa' },
            { tag: '{email}', title: 'E-mail da pessoa' },
            { tag: '{documento}', title: 'CPF / CNPJ da pessoa' },
            { tag: '{rg}', title: 'RG da pessoa' },
            { tag: '{nascimento}', title: 'Data de nascimento' },
        ],
        [
            { tag: '{endereco}', title: 'Endereço COMPLETO da pessoa' },
            { tag: '{rua}', title: 'Rua do endereço' },
            { tag: '{numero}', title: 'Número do endereço' },
            { tag: '{complemento}', title: 'Complemento do endereço' },
            { tag: '{bairro}', title: 'Bairro do endereço' },
            { tag: '{cidade}', title: 'Cidade do endereço' },
            { tag: '{estado}', title: 'Estado do endereço' },
            { tag: '{cep}', title: 'CEP do endereço' },
        ],
        [
            { tag: '{data}', title: 'Data atual' },
            { tag: '{dataporextenso}', title: 'Data atual por extenso' },
            { tag: '{hora}', title: 'Hora atual' },
            { tag: '{usuario}', title: 'Usuário logado que preencheu o formulário' },
            { tag: '{novapagina}', title: 'Quebra de página' },
        ],
        [{ tag: '{atendente}', title: 'Nome de quem preencheu o formulário' }],
        [
            { tag: '{compra}', title: 'Detalhamento de uma venda vinculada (apenas se o formulário estiver vinculado a uma venda)' },
            {
                tag: '{compraporextenso}',
                title: 'Detalhamento de uma venda vinculada com valor por extenso (apenas se o formulário estiver vinculado a uma venda)',
            },
            { tag: '{valordacompra}', title: 'Valor total da venda vinculada' },
            { tag: '{valordacompraporextenso}', title: 'Valor total da venda vinculada por extenso' },
            { tag: '{datacompra}', title: 'Data da venda vinculada' },
            { tag: '{vendedor}', title: 'Nome do(s) vendedor(es) da venda vinculada' },
        ],
        [
            {
                tag: '{pagamento}',
                title: 'Meios de pagamento da venda vinculada, ou valor pago da conta vinculada (apenas se o formulário estiver vinculado a uma venda ou conta)',
            },
            { tag: '{pagamentoporextenso}', title: 'Mesmo que {pagamento}, com valor por extenso' },
            { tag: '{valordopagamento}', title: 'Valor total pago' },
            { tag: '{valordopagamentoporextenso}', title: 'Valor total pago por extenso' },
        ],
        [
            { tag: '{databaixa}', title: 'Data em que a baixa da conta vinculada ocorreu (apenas se o formulário estiver vinculado a uma conta)' },
            { tag: '{descricao}', title: 'Descrição da conta vinculada' },
        ],
    ]

    let editorRef: TinyEditor
    let copiedTag: string | null = null
    let copiedTimer: ReturnType<typeof setTimeout>

    // Clicar na chave faz as duas coisas: insere no ponto onde o cursor está (evita o ctrl+V) e
    // deixa uma cópia na área de transferência, pra quem quiser colar em outro lugar.
    async function useTag(tag: string) {
        const inserted = editorRef?.insertAtCursor(tag) ?? false

        let copied = false
        try {
            await navigator.clipboard.writeText(tag)
            copied = true
        } catch {
            // Clipboard indisponível (contexto não seguro/permissão negada) — a inserção no editor
            // já resolve o caso principal, então não vale interromper com um erro.
        }

        if (!inserted && !copied) return
        copiedTag = tag
        clearTimeout(copiedTimer)
        copiedTimer = setTimeout(() => (copiedTag = null), 1500)
    }
</script>

<div class="block-preview">
    <TinyEditor bind:this={editorRef} bind:value={data.text} allowImage={true} />
    <details class="tags-help">
        <summary>Chaves de personalização disponíveis</summary>
        <div class="tags-list">
            {#each tagGroups as group}
                <div class="tags-group">
                    {#each group as item (item.tag)}
                        <button
                            type="button"
                            class="code tag-btn"
                            class:copied={copiedTag === item.tag}
                            title={copiedTag === item.tag ? 'Inserida no texto e copiada!' : item.title + ' — clique para inserir no texto (e copiar)'}
                            on:click={() => useTag(item.tag)}
                        >
                            {item.tag}
                        </button>
                    {/each}
                </div>
            {/each}
        </div>
    </details>
</div>

<style>
    .block-preview {
        padding-top: 4px;
    }
    .tags-help {
        margin-top: 8px;
        font-size: 0.85rem;
    }
    .tags-help summary {
        cursor: pointer;
        color: #868e96;
        user-select: none;
    }
    .tags-help summary:hover {
        color: inherit;
    }
    .tags-list {
        margin-top: 8px;
        display: flex;
        flex-direction: column;
        gap: 8px;
    }
    .tags-group {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
    }
    .tag-btn {
        background-color: rgba(173, 181, 189, 0.3);
        border: 1px solid rgba(173, 181, 189, 0.5);
        color: inherit;
        border-radius: 4px;
        padding: 2px 6px;
        font-family: monospace;
        font-size: 0.82rem;
        line-height: 1.5;
        cursor: pointer;
        transition: background-color 0.12s, border-color 0.12s, color 0.12s;
    }
    .tag-btn:hover {
        background-color: rgba(66, 99, 235, 0.18);
        border-color: var(--cs2-accent, #4263eb);
    }
    .tag-btn.copied {
        background-color: rgba(47, 158, 68, 0.22);
        border-color: #2f9e44;
        color: #2f9e44;
    }
</style>
